import { AtSign, Edit, Phone, Smartphone, AlertTriangle } from "lucide-react";
import React, { useState, useEffect } from "react";

import { editAboutMeProfile, EditAboutMeType } from "@/api/profile";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useProfile } from "@/hooks/server-state/use-profile";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

import SaveButton from "../_components/save-button";
import Textarea from "../_components/textarea";
import TextfieldRepeater from "../_components/textfieald-reapeter";

const EditProfileAboutMe = () => {
  const { data: profileData } = useProfile();

  // Enhanced error logging for development
  useEffect(() => {
    console.log("EditProfileAboutMe - Profile data:", profileData);
  }, [profileData]);

  const [editingField, setEditingField] = useState<string | null>(null);
  const [showScopusModal, setShowScopusModal] = useState(false);
  const [originalScopusId, setOriginalScopusId] = useState<string>("");
  const [editData, setEditData] = useState<EditAboutMeType>({
    id: undefined,
    universityEmail: "",
    personalEmail: "",
    phone: "",
    mobileNumber: "",
    biographyEn: "",
    biographyFa: "",
    lastName: "",
    firstName: "",
    orcid: "",
    googleScholar: "",
    scopus: "",
    isi: "",
    isc: "",
    nickName: [],
    affilations: [],
    moreEmails: []
  });

  useEffect(() => {
    if (profileData) {
      const currentScopusId = profileData.scopusID || "";
      setOriginalScopusId(currentScopusId);

      setEditData({
        id: profileData.id,
        universityEmail: profileData.universityEmail || "",
        personalEmail: profileData.personalEmail || "",
        phone: profileData.phone || "",
        mobileNumber: profileData.mobileNumber || "",
        biographyEn: profileData.biographyEn || "",
        biographyFa: profileData.biographyFa || "",
        lastName: profileData.lastNameEn || "",
        firstName: profileData.firstNameEn || "",
        orcid: profileData.orcidId || "",
        googleScholar: profileData.googleScholarID || "",
        scopus: currentScopusId,
        isi: profileData.webOfScienceID || "",
        isc: profileData.socialMedia?.isc || "",
        nickName: Array.isArray(profileData.nickNames)
          ? profileData.nickNames.map((n: { name: string }) => ({
              name: n?.name || ""
            }))
          : [],
        affilations: Array.isArray(profileData.affilations)
          ? profileData.affilations.map((n: { title: string }) => ({
              title: n?.title || ""
            }))
          : [],
        moreEmails: Array.isArray(profileData.moreEmails)
          ? profileData.moreEmails.map((n: { email: string }) => ({
              email: n?.email || ""
            }))
          : []
      });
    }
  }, [profileData]);

  const queryClient = useQueryClient();

  const mutateData = useMutation({
    mutationFn: (data: EditAboutMeType) => editAboutMeProfile(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["profile"] })
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // بررسی تغییر Scopus ID
    const scopusChanged = editData.scopus !== originalScopusId;

    if (scopusChanged) {
      // نمایش مدال تأیید
      setShowScopusModal(true);
    } else {
      // ذخیره بدون مدال
      mutateData.mutate(editData);
      setEditingField(null);
    }
  };

  const handleScopusConfirm = () => {
    // تأیید تغییر Scopus - ذخیره همه داده‌ها از جمله Scopus
    mutateData.mutate(editData);
    setShowScopusModal(false);
    setEditingField(null);
  };

  const handleScopusCancel = () => {
    // لغو تغییر Scopus - ذخیره همه داده‌ها به جز Scopus
    const dataWithoutScopusChange = {
      ...editData,
      scopus: originalScopusId // بازگردانی به مقدار قبلی
    };
    mutateData.mutate(dataWithoutScopusChange);
    setShowScopusModal(false);
    setEditingField(null);

    // بازگشت editData به حالت قبل
    setEditData((prev) => ({
      ...prev,
      scopus: originalScopusId
    }));
  };

  return (
    <form onSubmit={handleSubmit}>
      <div id="parent" className="w-full bg-[#E4EBF3] px-12 py-5">
        <div id="phone-and-email" className="">
          <div className="flex w-full flex-col flex-wrap border-b border-b-slate-300 pb-5 lg:flex-row">
            {/* بخش تلفن */}
            <div id="phone-section" className="w-1/2 space-y-4">
              <div className="flex items-center gap-x-2 lg:gap-x-10">
                <div className="flex w-32 items-center gap-x-1">
                  <Phone color="#0099A5" size={10} />
                  <p className="w-32 text-xs">تلفن ثابت:</p>
                </div>
                <div className="flex items-center gap-x-2">
                  <Input
                    defaultValue={profileData?.phone || ""}
                    onChange={(e) => {
                      setEditData((prev: any) => ({
                        ...prev,
                        phone: e.target.value
                      }));
                    }}
                    className="h-full w-28 rounded-md border border-slate-300 p-1 !text-xs disabled:border-transparent disabled:bg-transparent disabled:text-black disabled:opacity-100"
                    dir="ltr"
                    disabled={editingField !== "phone"}
                  />
                  <button
                    type="button"
                    onClick={() => setEditingField("phone")}
                  >
                    <Edit size={16} />
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-x-2 lg:gap-x-10">
                <div className="flex w-32 items-center gap-x-1">
                  <Smartphone color="#0099A5" size={10} />
                  <p className="w-32 text-xs">تلفن همراه:</p>
                </div>
                <div className="flex items-center gap-x-2">
                  <Input
                    defaultValue={profileData?.mobileNumber || ""}
                    onChange={(e) => {
                      setEditData((prev: any) => ({
                        ...prev,
                        mobileNumber: e.target.value
                      }));
                    }}
                    className="h-full w-28 rounded-md border border-slate-300 p-1 !text-xs disabled:border-transparent disabled:bg-transparent disabled:text-black disabled:opacity-100"
                    dir="ltr"
                    disabled={editingField !== "mobileNumber"}
                  />
                  <button
                    type="button"
                    onClick={() => setEditingField("mobileNumber")}
                  >
                    <Edit size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* بخش ایمیل */}
            <div id="email-section" className="mt-4 w-1/2 space-y-4">
              <div className="flex items-center gap-x-2 lg:gap-x-10">
                <div className="flex w-32 items-center gap-x-1">
                  <AtSign color="#0099A5" size={10} />
                  <p className="w-32 text-xs">ایمیل دانشگاهی:</p>
                </div>
                <div className="flex items-center gap-x-2">
                  <Input
                    defaultValue={profileData?.universityEmail || ""}
                    onChange={(e) => {
                      setEditData((prev: any) => ({
                        ...prev,
                        universityEmail: e.target.value
                      }));
                    }}
                    className="h-full w-28 rounded-md border border-slate-300 p-1 !text-xs disabled:border-transparent disabled:bg-transparent disabled:text-black disabled:opacity-100"
                    dir="ltr"
                    disabled={editingField !== "universityEmail"}
                  />
                  <button
                    type="button"
                    onClick={() => setEditingField("universityEmail")}
                  >
                    <Edit size={16} />
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-x-2 lg:gap-x-10">
                <div className="flex w-32 items-center gap-x-1">
                  <AtSign color="#0099A5" size={10} />
                  <p className="w-32 text-xs">ایمیل جایگزین:</p>
                </div>
                <div className="flex items-center gap-x-2">
                  <Input
                    defaultValue={profileData?.personalEmail || ""}
                    onChange={(e) => {
                      setEditData((prev: any) => ({
                        ...prev,
                        personalEmail: e.target.value
                      }));
                    }}
                    className="h-full w-28 rounded-md border border-slate-300 p-1 !text-xs disabled:border-transparent disabled:bg-transparent disabled:text-black disabled:opacity-100"
                    dir="ltr"
                    disabled={editingField !== "personalEmail"}
                  />
                  <button
                    type="button"
                    onClick={() => setEditingField("personalEmail")}
                  >
                    <Edit size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* لینک‌های خارجی */}
          <div
            className="mt-5 grid w-full grid-cols-1 gap-x-5 gap-y-5 sm:grid-cols-2 lg:grid-cols-5"
            dir="ltr"
          >
            <div className="flex items-center gap-x-2">
              <img
                src="/dashboard/images/icons/scholar.png"
                alt="google scholar"
                width="14px"
              />
              Google
              <button type="button" onClick={() => setEditingField("scholar")}>
                <Edit size={16} />
              </button>
              <Input
                defaultValue={profileData?.googleScholarID || ""}
                onChange={(e) => {
                  setEditData((prev: any) => ({
                    ...prev,
                    googleScholar: e.target.value
                  }));
                }}
                className="w- h-full rounded-md border border-slate-300 p-1 !text-xs disabled:border-transparent disabled:bg-transparent disabled:text-black disabled:opacity-100"
                disabled={editingField !== "scholar"}
              />
            </div>
            <div className="flex items-center gap-x-2">
              <img
                src="/dashboard/images/icons/orcid.svg"
                alt="orcid"
                width="14px"
              />
              Orcid
              <button type="button" onClick={() => setEditingField("orcid")}>
                <Edit size={16} />
              </button>
              <Input
                defaultValue={profileData?.orcidId || ""}
                onChange={(e) => {
                  setEditData((prev: any) => ({
                    ...prev,
                    orcid: e.target.value
                  }));
                }}
                className="w- h-full rounded-md border border-slate-300 p-1 !text-xs disabled:border-transparent disabled:bg-transparent disabled:text-black disabled:opacity-100"
                disabled={editingField !== "orcid"}
              />
            </div>
            <div className="flex items-center gap-x-2">
              <img
                src="/dashboard/images/icons/Scopus.svg"
                alt="scopus"
                width="14px"
              />
              Scopus
              <button type="button" onClick={() => setEditingField("scopus")}>
                <Edit size={16} />
              </button>
              <Input
                value={editData.scopus}
                onChange={(e) => {
                  setEditData((prev: any) => ({
                    ...prev,
                    scopus: e.target.value
                  }));
                }}
                className="w- h-full rounded-md border border-slate-300 p-1 !text-xs disabled:border-transparent disabled:bg-transparent disabled:text-black disabled:opacity-100"
                disabled={editingField !== "scopus"}
              />
            </div>
            <div className="flex items-center gap-x-2">
              <img
                src="/dashboard/images/icons/isi.png"
                alt="isi"
                width="14px"
              />
              ISI
              <button type="button" onClick={() => setEditingField("isi")}>
                <Edit size={16} />
              </button>
              <Input
                defaultValue={profileData?.webOfScienceID || ""}
                onChange={(e) => {
                  setEditData((prev: any) => ({
                    ...prev,
                    isi: e.target.value
                  }));
                }}
                className="w- h-full rounded-md border border-slate-300 p-1 !text-xs disabled:border-transparent disabled:bg-transparent disabled:text-black disabled:opacity-100"
                disabled={editingField !== "isi"}
              />
            </div>
            <div className="flex items-center gap-x-2">
              <img
                src="/dashboard/images/icons/isc.png"
                alt="isc"
                width="14px"
              />
              ISC
              <button type="button" onClick={() => setEditingField("isc")}>
                <Edit size={16} />
              </button>
              <Input
                defaultValue={profileData?.socialMedia?.isc || ""}
                onChange={(e) => {
                  setEditData((prev: any) => ({
                    ...prev,
                    isc: e.target.value
                  }));
                }}
                className="w- h-full rounded-md border border-slate-300 p-1 !text-xs disabled:border-transparent disabled:bg-transparent disabled:text-black disabled:opacity-100"
                disabled={editingField !== "isc"}
              />
            </div>
          </div>
        </div>
      </div>

      {/* بیوگرافی */}
      <div className="px-10 py-6">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2">
          <div>
            <Textarea
              defaultValue={profileData?.biographyFa || ""}
              onChange={(e) => {
                setEditData((prev: any) => ({
                  ...prev,
                  biographyFa: e.target.value
                }));
              }}
              className="h-64 w-full"
              label={<>درباره من</>}
            />
          </div>
          <div dir="ltr">
            <Textarea
              defaultValue={profileData?.biographyEn || ""}
              onChange={(e) => {
                setEditData((prev: any) => ({
                  ...prev,
                  biographyEn: e.target.value
                }));
              }}
              className="h-64 w-full"
              label={<>About me</>}
            />
          </div>
        </div>
      </div>
      {/* بخش Nickname */}
      <div className="px-14 py-4">
        <h3 className="mb-3 text-sm font-semibold">
          نام‌ها متفاوت{" "}
          <span className="text-sm text-gray-500">(فارسی و انگلیسی)</span>
        </h3>
        <p className="mb-4 mt-4 text-sm text-slate-700">
          لطفا در صورتی که دارای نامی با نوع نوشتار متفاوت نسبت به نام اصلی خود
          دارید لطفا وارد نمایید(به عنوان مثال نعمت الله - نعمت اله - نعمت ا..)
        </p>
        <TextfieldRepeater
          defaultValues={profileData?.nickNames?.map((n) => n.name) || []}
          onValuesChange={(values: string[]) => {
            setEditData((prev: any) => ({
              ...prev,
              nickName: values.map((value: string) => ({ name: value || "" }))
            }));
          }}
        />
        <div className="mt-8 space-y-3">
          <h3 className="mb-3 text-sm font-semibold">
            وابستگی سازمانی متفاوت{" "}
            <span className="text-sm text-gray-500">(فارسی و انگلیسی)</span>
          </h3>
          <p className="mb-4 mt-4 text-sm text-slate-700">
            لطفا در صورتی که دارای وابستگی سازمانی با نوع نوشتار متفاوت نسبت به
            وابستگی سازمانی اصلی خود دارید لطفا وارد نمایید(این داده ها صرفا
            برای گردآوری خودکار بروندادهای شما می باشد)
          </p>
          <TextfieldRepeater
            defaultValues={profileData?.affilations?.map((n) => n.title) || []}
            onValuesChange={(values: string[]) => {
              setEditData((prev: any) => ({
                ...prev,
                affilations: values.map((value: string) => ({
                  title: value || ""
                }))
              }));
            }}
          />
        </div>

        <div className="mt-8 space-y-3">
          <h3 className="mb-3 text-sm font-semibold">پست الکترونیکی متفاوت</h3>
          <p className="mb-4 mt-4 text-sm text-slate-700">
            لطفا در صورتی که دارای پست الکترونیکی با نوع نوشتار متفاوت نسبت به
            پست الکترونیکی اصلی خود دارید لطفا وارد نمایید
          </p>
          <TextfieldRepeater
            defaultValues={profileData?.moreEmails?.map((n) => n.email) || []}
            onValuesChange={(values: string[]) => {
              setEditData((prev: any) => ({
                ...prev,
                moreEmails: values.map((value: string) => ({
                  email: value || ""
                }))
              }));
            }}
          />
        </div>
      </div>
      <SaveButton />

      {/* مدال تأیید تغییر Scopus ID */}
      <Dialog open={showScopusModal} onOpenChange={setShowScopusModal}>
        <DialogContent className="max-w-2xl">
          <div className="mb-8 flex w-full justify-center">
            <div className="relative flex size-20 items-center justify-center rounded-full bg-amber-500 text-white">
              <AlertTriangle size={40} />
              <span className="absolute -z-10 inline-flex h-4/5 w-4/5 animate-ping rounded-full bg-amber-300 opacity-75 duration-1000"></span>
            </div>
          </div>

          <div className="flex flex-col items-center border-b border-b-slate-300 pb-5">
            <h3 className="text-2xl font-bold text-[#273646]">
              تأیید تغییر شناسه Scopus
            </h3>
          </div>

          <div className="space-y-4 px-2 py-4 text-right leading-7 text-gray-700">
            <p className="text-lg font-semibold text-gray-800">کاربر گرامی</p>

            <p className="text-base leading-relaxed">
              درخواست تغییر شناسه{" "}
              <span className="font-semibold">Scopus (Scopus ID)</span>{" "}
              ثبت‌گردیده است. توجّه داشته باشید که اعمال این تغییر، مستلزم
              به‌روزرسانی کلیه سوابق پژوهشی، مقالات و اطلاعات مرتبط با شناسه
              پیشین می‌باشد.
            </p>

            <p className="text-base leading-relaxed">
              پس از تأیید این تغییر، فرآیند استخراج مقالات و اطلاعات جدید آغاز
              خواهد شد و پس از اتمام به شما اطلاع‌رسانی می‌شود. لطفاً پیش از
              ادامه، از صحت و قطعیت شناسه واردشده اطمینان حاصل فرمایید.
            </p>

            <div className="mt-4 border-r-4 border-amber-500 bg-amber-50 p-4">
              <p className="text-base font-semibold text-gray-800">
                آیا از تغییر شناسه Scopus و به‌روزرسانی اطلاعات وابسته به آن
                اطمینان دارید؟
              </p>
            </div>
          </div>

          <div className="mt-4 flex w-full gap-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleScopusCancel}
              className="h-11 flex-1"
              disabled={mutateData.isPending}
            >
              لغو
            </Button>
            <Button
              type="button"
              onClick={handleScopusConfirm}
              className="h-11 flex-1 bg-primary hover:bg-primary/90"
              disabled={mutateData.isPending}
            >
              {mutateData.isPending ? "در حال ذخیره..." : "تأیید و ادامه"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </form>
  );
};
export default EditProfileAboutMe;
